export const LEGACY_USER_ROLES = [
  "admin",
  "salvia_operation_admin",
  "channel_admin",
  "channel_admin_multi",
  "channel_operator",
  "channel_consultant",
  "client_rh",
] as const;

export type LegacyUserRole = (typeof LEGACY_USER_ROLES)[number];
export type StructuralRole = "admin" | "channel" | "client";
export type ChannelScopeMode = "single" | "multi";
export type OperationalProfile =
  | "platform_master"
  | "platform_operations_manager"
  | "channel_manager"
  | "channel_operator"
  | "channel_readonly"
  | "client_rh";

export type StructuralRoleOption = {
  value: StructuralRole;
  label: string;
  description: string;
};

export type ChannelScopeOption = {
  value: ChannelScopeMode;
  label: string;
  description: string;
};

export type OperationalProfileOption = {
  value: OperationalProfile;
  label: string;
  description: string;
};

export type UserAccessModel = {
  structuralRole: StructuralRole;
  channelScopeMode: ChannelScopeMode;
  operationalProfile: OperationalProfile;
};

export const STRUCTURAL_ROLE_OPTIONS: readonly StructuralRoleOption[] = [
  {
    value: "admin",
    label: "Admin",
    description: "Usuário estrutural com alcance global da plataforma.",
  },
  {
    value: "channel",
    label: "Canal",
    description: "Usuário estrutural vinculado à operação de um ou mais canais.",
  },
  {
    value: "client",
    label: "Cliente",
    description: "Usuário estrutural vinculado a uma empresa cliente específica.",
  },
] as const;

export const CHANNEL_SCOPE_OPTIONS: readonly ChannelScopeOption[] = [
  {
    value: "single",
    label: "Canal único",
    description: "O usuário atua em um único canal por vínculo estrutural.",
  },
  {
    value: "multi",
    label: "Multicanal",
    description: "O usuário poderá alternar entre mais de um canal após o login.",
  },
] as const;

const OPERATIONAL_PROFILE_OPTIONS_BY_STRUCTURAL_ROLE: Record<StructuralRole, readonly OperationalProfileOption[]> = {
  admin: [
    {
      value: "platform_master",
      label: "Master da Plataforma",
      description: "Mantém a compatibilidade com o role técnico administrativo global.",
    },
    {
      value: "platform_operations_manager",
      label: "Admin de Operação Sálvia",
      description: "Mantém a compatibilidade com o papel administrativo operacional, sem herdar os acessos sensíveis de TI.",
    },
  ],
  channel: [
    {
      value: "channel_manager",
      label: "Gestor do Canal",
      description: "Mantém a governança administrativa do canal no legado.",
    },
    {
      value: "channel_operator",
      label: "Operador do Canal",
      description: "Mantém a capacidade operacional intermediária já existente.",
    },
    {
      value: "channel_readonly",
      label: "Leitura do Canal",
      description: "Mantém a visualização restrita já suportada no portal.",
    },
  ],
  client: [
    {
      value: "client_rh",
      label: "RH do Cliente",
      description: "Mantém a compatibilidade com o role técnico do cliente final.",
    },
  ],
};

const LEGACY_ROLE_LABELS: Record<LegacyUserRole, string> = {
  admin: "Admin Master Sálvia",
  salvia_operation_admin: "Admin de Operação Sálvia",
  channel_admin: "Admin Master Canal",
  channel_admin_multi: "Admin Master Multicanal",
  channel_operator: "Operador Canal",
  channel_consultant: "Consultor Canal",
  client_rh: "Usuário RH (Cliente)",
};

export function getDefaultOperationalProfile(structuralRole: StructuralRole): OperationalProfile {
  if (structuralRole === "admin") return "platform_master";
  if (structuralRole === "channel") return "channel_manager";
  return "client_rh";
}

export function getOperationalProfileOptions(structuralRole: StructuralRole): readonly OperationalProfileOption[] {
  return OPERATIONAL_PROFILE_OPTIONS_BY_STRUCTURAL_ROLE[structuralRole];
}

export function deriveAccessModelFromLegacyRole(role: LegacyUserRole): UserAccessModel {
  if (role === "admin") {
    return {
      structuralRole: "admin",
      channelScopeMode: "single",
      operationalProfile: "platform_master",
    };
  }

  if (role === "salvia_operation_admin") {
    return {
      structuralRole: "admin",
      channelScopeMode: "single",
      operationalProfile: "platform_operations_manager",
    };
  }

  if (role === "channel_admin_multi") {
    return {
      structuralRole: "channel",
      channelScopeMode: "multi",
      operationalProfile: "channel_manager",
    };
  }

  if (role === "channel_admin") {
    return {
      structuralRole: "channel",
      channelScopeMode: "single",
      operationalProfile: "channel_manager",
    };
  }

  if (role === "channel_operator") {
    return {
      structuralRole: "channel",
      channelScopeMode: "single",
      operationalProfile: "channel_operator",
    };
  }

  if (role === "channel_consultant") {
    return {
      structuralRole: "channel",
      channelScopeMode: "single",
      operationalProfile: "channel_readonly",
    };
  }

  return {
    structuralRole: "client",
    channelScopeMode: "single",
    operationalProfile: "client_rh",
  };
}

export function resolveLegacyRoleFromAccessModel(model: UserAccessModel): LegacyUserRole {
  if (model.structuralRole === "admin") {
    return model.operationalProfile === "platform_operations_manager" ? "salvia_operation_admin" : "admin";
  }

  if (model.structuralRole === "client") return "client_rh";

  if (model.operationalProfile === "channel_operator") return "channel_operator";
  if (model.operationalProfile === "channel_readonly") return "channel_consultant";
  if (model.channelScopeMode === "multi") return "channel_admin_multi";
  return "channel_admin";
}

export function getLegacyRoleLabel(role: LegacyUserRole): string {
  return LEGACY_ROLE_LABELS[role];
}

export function getStructuralRoleLabel(structuralRole: StructuralRole): string {
  return STRUCTURAL_ROLE_OPTIONS.find((option) => option.value === structuralRole)?.label ?? structuralRole;
}

export function getOperationalProfileLabel(operationalProfile: OperationalProfile): string {
  return Object.values(OPERATIONAL_PROFILE_OPTIONS_BY_STRUCTURAL_ROLE)
    .flat()
    .find((option) => option.value === operationalProfile)?.label ?? operationalProfile;
}
